import React from "react";

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    const date = new Date(value);
    const datePart = new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric" }).format(date);
    const timePart = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: true })
      .format(date).replace(":", ".").replace(/\s+/g, " ").trim();
    return `${datePart} ${timePart}`;
  } catch { return String(value); }
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) return "-";
  const fmt = (value) => {
    if (!value) return "";
    try { return new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value)); }
    catch { return String(value); }
  };
  if (startDate && endDate) return `${fmt(startDate)} to ${fmt(endDate)}`;
  return fmt(startDate) || fmt(endDate) || "-";
};

const toText = (value, fallback = "-") => { const t = String(value ?? "").trim(); return t || fallback; };

export function TicketsSummaryPdfDocument({
  components, logoUrl, companyInfo, filters, report, generatedAt,
}) {
  const { Document, Page, StyleSheet, Text, View, Image } = components;

  const styles = StyleSheet.create({
    page: {
      paddingTop: 32,
      paddingBottom: 30,
      paddingHorizontal: 32,
      backgroundColor: "#ffffff",
      fontSize: 9,
      color: "#0f172a",
    },

    /* ── Header ── */
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingBottom: 10,
      borderBottomWidth: 1.5,
      borderBottomColor: "#0f172a",
      marginBottom: 12,
    },
    brandRow: { flexDirection: "column", alignItems: "flex-start" },
    logo: { width: 100, objectFit: "contain", marginBottom: 2 },
    companyName: { fontSize: 15, fontWeight: 700, color: "#0f172a", letterSpacing: -0.2 },
    companyTagline: { fontSize: 8, color: "#64748b", marginTop: 1, letterSpacing: 0.3 },
    contactBlock: { alignItems: "flex-start" },
    contactRow: { flexDirection: "row", alignItems: "center", marginBottom: 3 },
    contactLabel: { fontSize: 8, fontWeight: 700, color: "#0f172a", width: 52 },
    contactLine: { fontSize: 8, color: "#475569" },

    /* ── Meta bar ── */
    metaBar: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#f8fafc",
      borderWidth: 1,
      borderColor: "#e2e8f0",
      borderRadius: 6,
      paddingVertical: 7,
      paddingHorizontal: 11,
      marginBottom: 14,
    },
    metaLeft: { flexDirection: "column" },
    metaReportName: { fontSize: 11, fontWeight: 700, color: "#0f172a", marginBottom: 1 },
    metaFilter: { fontSize: 7.8, color: "#475569", lineHeight: 1.55 },
    metaRight: { alignItems: "flex-end" },
    metaGenLabel: { fontSize: 7, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 1 },
    metaGenValue: { fontSize: 8, color: "#334155" },

    /* ── Table ── */
    table: {
      borderWidth: 1,
      borderColor: "#e2e8f0",
      borderRadius: 7,
      overflow: "hidden",
    },
    tHead: {
      flexDirection: "row",
      borderBottomWidth: 1.5,
      borderBottomColor: "#0f172a",
      paddingVertical: 7,
      paddingHorizontal: 4,
    },
    tHeadCell: {
      fontSize: 7.4,
      fontWeight: 700,
      color: "#0f172a",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      paddingHorizontal: 8,
    },
    tRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#f1f5f9",
      paddingVertical: 7,
      paddingHorizontal: 4,
    },
    tRowAlt: { backgroundColor: "#f8fafc" },
    tRowLast: { borderBottomWidth: 0 },
    cell: { fontSize: 8.2, color: "#1e293b", lineHeight: 1.45, paddingHorizontal: 8 },
    cellBold: { fontSize: 8.2, fontWeight: 700, color: "#0f172a", lineHeight: 1.45, paddingHorizontal: 8 },
    cellCenter: { fontSize: 8.2, fontWeight: 700, color: "#0f172a", lineHeight: 1.45, paddingHorizontal: 8, textAlign: "center" },

    /* ── Empty / Footer ── */
    emptyBox: {
      borderWidth: 1, borderColor: "#e2e8f0", borderRadius: 7,
      paddingVertical: 20, alignItems: "center",
    },
    emptyText: { fontSize: 9, color: "#94a3b8" },
    footer: {
      marginTop: 10, paddingTop: 8,
      borderTopWidth: 1, borderTopColor: "#e2e8f0",
      flexDirection: "row", justifyContent: "space-between", alignItems: "center",
    },
    footerText: { fontSize: 7.6, color: "#94a3b8" },
  });

  const filterParts = [
    `Period: ${formatDateRange(filters?.startDate, filters?.endDate)}`,
    filters?.organizationName && filters.organizationName !== "All Organizations" ? `Org: ${filters.organizationName}` : null,
    filters?.employeeName && filters.employeeName !== "All Employees" ? `Employee: ${filters.employeeName}` : null,
    filters?.customerName && filters.customerName !== "All Customers" ? `Customer: ${filters.customerName}` : null,
    filters?.customerUserName && filters.customerUserName !== "All Customer Users" ? `User: ${filters.customerUserName}` : null,
    filters?.status && filters.status !== "All Statuses" ? `Status: ${filters.status}` : null,
    filters?.customerCode && filters.customerCode !== "All" ? `Code: ${filters.customerCode}` : null,
  ].filter(Boolean);

  const totalItems = report?.items?.length || 0;
  const totalTickets = (report?.items || []).reduce((s, item) => s + (item.ticketsCount || 0), 0);

  return (
    <Document
      title="Tickets Summary Report"
      author="BanglaFire CRM"
      subject="Tickets summary"
      creator="BanglaFire CRM"
      producer="BanglaFire CRM"
    >
      <Page size="A4" style={styles.page}>

        {/* ── HEADER ── */}
        <View style={styles.header}>
          <View style={styles.brandRow}>
            {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : null}
            <Text style={styles.companyName}>{toText(companyInfo?.name, "BanglaFire CRM")}</Text>
            <Text style={styles.companyTagline}>Customer Relationship Management</Text>
          </View>
          <View style={styles.contactBlock}>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Address :</Text>
              <Text style={styles.contactLine}>39/9 A, Senpara Parbata, Mirpur, Dhaka-1216</Text>
            </View>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Mobile :</Text>
              <Text style={styles.contactLine}>(+880) 9639222999  |  1709927934</Text>
            </View>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Email :</Text>
              <Text style={styles.contactLine}>md@banglafire.com  |  support@banglafire.com</Text>
            </View>
          </View>
        </View>

        {/* ── META BAR ── */}
        <View style={styles.metaBar}>
          <View style={styles.metaLeft}>
            <Text style={styles.metaReportName}>Tickets Summary Report</Text>
            {filterParts.length > 0 && (
              <Text style={styles.metaFilter}>{filterParts.join("   •   ")}</Text>
            )}
          </View>
          <View style={styles.metaRight}>
            <Text style={styles.metaGenLabel}>Generated</Text>
            <Text style={styles.metaGenValue}>{formatDateTime(generatedAt)}</Text>
            <Text style={[styles.metaGenLabel, { marginTop: 4 }]}>Total Tickets</Text>
            <Text style={[styles.metaGenValue, { fontWeight: 700, color: "#0f172a" }]}>{totalTickets}</Text>
          </View>
        </View>

        {/* ── TABLE ── */}
        {totalItems > 0 ? (
          <View style={styles.table}>
            <View style={styles.tHead}>
              <Text style={[styles.tHeadCell, { width: "24%" }]}>Organization</Text>
              <Text style={[styles.tHeadCell, { width: "24%" }]}>Assigned To</Text>
              <Text style={[styles.tHeadCell, { width: "22%" }]}>Customer</Text>
              <Text style={[styles.tHeadCell, { width: "22%" }]}>Customer User</Text>
              <Text style={[styles.tHeadCell, { width: "8%", textAlign: "center" }]}>Tickets</Text>
            </View>
            {report.items.map((item, idx) => {
              const isLast = idx === report.items.length - 1;
              return (
                <View
                  key={`row-${idx}`}
                  style={[styles.tRow, idx % 2 === 1 ? styles.tRowAlt : null, isLast ? styles.tRowLast : null]}
                  wrap={false}
                >
                  <Text style={[styles.cell, { width: "24%" }]}>{toText(item.organizationName)}</Text>
                  <Text style={[styles.cell, { width: "24%" }]}>{toText(item.employeeName, "Unassigned")}</Text>
                  <Text style={[styles.cellBold, { width: "22%" }]}>{toText(item.customerName)}</Text>
                  <Text style={[styles.cell, { width: "22%" }]}>{toText(item.customerUserName, "Unknown User")}</Text>
                  <Text style={[styles.cellCenter, { width: "8%" }]}>{String(item.ticketsCount || 0)}</Text>
                </View>
              );
            })}
          </View>
        ) : (
          <View style={styles.emptyBox}>
            <Text style={styles.emptyText}>No tickets matched the selected filters.</Text>
          </View>
        )}

        {/* ── FOOTER ── */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>Generated on {formatDateTime(generatedAt)}</Text>
          <Text style={styles.footerText}>BanglaFire CRM  |  Confidential</Text>
        </View>
      </Page>
    </Document>
  );
}
